/**
 * Route diagnostics — developer-safe.
 *
 * When a report link misbehaves, the first question is always "which route
 * actually served this?". This records that, in memory only, and logs it to
 * the console in dev. It never records answers, tokens or user data — only
 * the path, the search string and the name of the handler component.
 */
export interface RouteHit {
  at: string;
  path: string;
  search: string;
  handler: string;
  note?: string;
}

const hits: RouteHit[] = [];
const MAX = 25;
const listeners = new Set<() => void>();

export function isRouteDiagOn(): boolean {
  if (import.meta.env.DEV) return true;
  try {
    return new URLSearchParams(window.location.search).get("diag") === "1"
      || localStorage.getItem("wcc360.diag") === "1";
  } catch { return false; }
}

/** Record which handler served the current location. */
export function recordRouteHit(handler: string, note?: string) {
  if (!isRouteDiagOn()) return;
  const hit: RouteHit = {
    at: new Date().toISOString(),
    path: window.location.pathname,
    search: window.location.search,
    handler,
    note,
  };
  hits.unshift(hit);
  if (hits.length > MAX) hits.length = MAX;
  // eslint-disable-next-line no-console
  console.info(
    `[route] ${hit.handler} served ${hit.path}${hit.search}${note ? ` — ${note}` : ""}`,
  );
  listeners.forEach((l) => l());
}

export function routeHits(): RouteHit[] {
  return hits;
}

export function subscribeRouteHits(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
